import { OptionNameValuePair } from '@/src/lib/interfaces/types';
import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';

interface MultiSelectProps<T> {
    name: string;
    IconComponent: React.ElementType;
    options: OptionNameValuePair<T>[];
    selectedValues: T[];
    onSelectionChange: (selectedValues: T[]) => void;
    showSearch?: boolean;
    showClearSelection?: boolean;
    className?: string;
    buttonClassName?: string;
    dropdownClassName?: string;
    dropdownWidthClassName?: string;
}

function MultiSelect<T>({
    name,
    options,
    selectedValues,
    IconComponent,
    onSelectionChange,
    showSearch = true,
    showClearSelection = true,
    className,
    buttonClassName,
    dropdownClassName,
    dropdownWidthClassName,
}: MultiSelectProps<T>){

    const [isOpen, setIsOpen] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleIsOpen = () => {
        setIsOpen(current => !current)
    }

    const [visibleOptions, setVisibleOptions] = useState<OptionNameValuePair<T>[]>(options)

    useEffect(() => {
        setVisibleOptions(options);
    }, [options]);

    const filterOptions = (event: ChangeEvent<HTMLInputElement>) => {
        const filteredOptions = options.filter(option => option.name.toLowerCase().startsWith(event.target.value.toLowerCase()));
        setVisibleOptions(filteredOptions);
    }

    const selectOption = (selectedValue: T) => {
        const updatedSelectedValues = [...selectedValues, selectedValue];
        onSelectionChange(updatedSelectedValues)
    }

    const unselectOption = (unselectedValue: T) => {
        const updatedSelectedValues = selectedValues.filter((option) => option !== unselectedValue)
        onSelectionChange(updatedSelectedValues)
    }

    const clearSelections = () => {
        onSelectionChange([])
    }

    return (
        <div className={cn("relative", className)} ref={dropdownRef}>
            <button
                className={
                    isOpen?
                    cn('px-3 py-1.5 border border-blue-500 bg-blue-50 text-blue-600 rounded-md text-sm flex items-center', buttonClassName):
                    cn('px-3 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 rounded-md text-sm flex items-center', buttonClassName)
                }
                onClick={() => {toggleIsOpen()}}
            >
                <IconComponent className = 'mr-2 h-4 w-4'/>
                {name}
                {selectedValues.length > 0 && <span className="ml-1.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full w-6 h-6 flex items-center justify-center">{selectedValues.length}</span>}
                {isOpen?<ChevronUp className='ml-2 h-4 w-4'/>:<ChevronDown className='ml-2 h-4 w-4'/>}
            </button>
            {isOpen &&
                <div
                  className={cn(
                    'absolute mt-1 z-10 bg-white border border-gray-200 rounded-md shadow-lg',
                    dropdownWidthClassName ?? 'w-[17.5rem]',
                    dropdownClassName
                  )}
                >
                    <div className='p-2'>
                        <div className="text-xs font-medium text-gray-500 mb-2 px-2">SELECT {name.toUpperCase()}</div>
                        {showSearch && (
                            <div className="p-2 mb-2">
                            <input 
                                placeholder={"Search " + name.toLowerCase()} 
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" 
                                type="text" 
                                onChange={filterOptions}
                            />
                        </div>
                        )}
                        
                        <div className='max-h-60 overflow-y-auto'>
                            {visibleOptions.map(option => (
                                selectedValues.includes(option.value)?
                                    <button key={option.name} className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-100 flex items-center" onClick={() => unselectOption(option.value)}>
                                        <div className="w-4 h-4 border rounded flex items-center justify-center mr-2 bg-blue-500 border-blue-500">
                                            <Check className="text-white" />
                                        </div>
                                        {option.display_component?option.display_component:option.name}
                                    </button> 
                                :
                                    <button key={option.name} className='w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-100 flex items-center' onClick={() => selectOption(option.value)}>
                                        <div className='w-4 h-4 border rounded flex items-center justify-center mr-2 border-gray-300'/>
                                        {option.display_component?option.display_component:option.name}
                                    </button>
                            ))}
                        </div>
                        {showClearSelection &&  selectedValues.length > 0 && 
                            <div className="p-2 border-t border-gray-200 mt-2">
                                <button className="w-full text-left px-2 py-1.5 text-sm text-red-500 hover:bg-gray-100 rounded flex items-center" onClick={clearSelections}>
                                    <X className='h-4 w-4'/>
                                    Clear {name.toLowerCase()} filters
                                </button>
                            </div>
                        }
                    </div>
                </div>
            }

        </div>
    )
}

export { MultiSelect };